<script setup>
import { reactive, computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';

const props = defineProps({
  isLoading: { type: Boolean, default: false },
});

const emit = defineEmits(['create']);

const { t } = useI18n();

const dialogRef = ref(null);

// The languages this installation actually sends in. Meta's full list is ~80
// entries and a combobox of them is worse than a short one.
const LANGUAGES = ['es_AR', 'es', 'es_ES', 'en_US', 'en', 'pt_BR'];

// Same regex the composer uses to find variables in a template body, so what is
// asked for here is what gets filled there.
const PLACEHOLDER = /\{\{\s*([\w\d_]+)\s*\}\}/g;

const initialState = {
  name: '',
  category: 'UTILITY',
  language: 'es_AR',
  body: '',
};

const state = reactive({ ...initialState });
const examples = reactive({});

// Meta's two reviewable categories, spelled out rather than mapped: a dynamic
// i18n key hides the string from the translation tooling.
// AUTHENTICATION is left out on purpose -- it only accepts its own fixed body.
const categoryOptions = computed(() => [
  {
    value: 'UTILITY',
    label: t(
      'MARKETING.CAMPAIGN.FORM.WHATSAPP.NEW_TEMPLATE.CATEGORIES.UTILITY'
    ),
  },
  {
    value: 'MARKETING',
    label: t(
      'MARKETING.CAMPAIGN.FORM.WHATSAPP.NEW_TEMPLATE.CATEGORIES.MARKETING'
    ),
  },
]);

const languageOptions = computed(() =>
  LANGUAGES.map(value => ({ value, label: value }))
);

// Meta only accepts lowercase letters, numbers and underscores and does not
// normalise on its side, so a name with a space comes back as an error. Doing it
// here means the operator sees what the name will be before sending it.
const normalizedName = computed(() =>
  state.name
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9_]+/g, '_')
    .replace(/^_+|_+$/g, '')
);

const variables = computed(() => {
  const matches = [...state.body.matchAll(PLACEHOLDER)];
  return [...new Set(matches.map(([, key]) => key))];
});

// Meta rejects a body with variables and no example values, so the form asks for
// one per variable instead of letting that round trip fail.
const isValid = computed(
  () =>
    normalizedName.value.length > 0 &&
    state.body.trim().length > 0 &&
    variables.value.every(variable => examples[variable]?.trim())
);

const previewBody = computed(() =>
  state.body.replace(
    PLACEHOLDER,
    (match, key) => examples[key]?.trim() || match
  )
);

watch(variables, list => {
  Object.keys(examples).forEach(key => {
    if (!list.includes(key)) delete examples[key];
  });
  list.forEach(variable => {
    if (examples[variable] === undefined) examples[variable] = '';
  });
});

const reset = () => {
  Object.assign(state, initialState);
  Object.keys(examples).forEach(key => delete examples[key]);
};

const handleConfirm = () => {
  if (!isValid.value) return;

  emit('create', {
    name: normalizedName.value,
    category: state.category,
    language: state.language,
    body: state.body.trim(),
    examples: { ...examples },
  });
};

defineExpose({ dialogRef, reset });
</script>

<template>
  <Dialog
    ref="dialogRef"
    type="edit"
    width="xl"
    overflow-y-auto
    :title="t('MARKETING.CAMPAIGN.FORM.WHATSAPP.NEW_TEMPLATE.TITLE')"
    :description="
      t('MARKETING.CAMPAIGN.FORM.WHATSAPP.NEW_TEMPLATE.DESCRIPTION')
    "
    :confirm-button-label="
      t('MARKETING.CAMPAIGN.FORM.WHATSAPP.NEW_TEMPLATE.SUBMIT')
    "
    :disable-confirm-button="!isValid || props.isLoading"
    :is-loading="props.isLoading"
    @confirm="handleConfirm"
  >
    <div class="flex flex-col gap-4">
      <Input
        v-model="state.name"
        :label="t('MARKETING.CAMPAIGN.FORM.WHATSAPP.NEW_TEMPLATE.NAME')"
        :placeholder="
          t('MARKETING.CAMPAIGN.FORM.WHATSAPP.NEW_TEMPLATE.NAME_PLACEHOLDER')
        "
        :message="
          normalizedName && normalizedName !== state.name.trim()
            ? t(
                'MARKETING.CAMPAIGN.FORM.WHATSAPP.NEW_TEMPLATE.NAME_NORMALIZED',
                {
                  name: normalizedName,
                }
              )
            : t('MARKETING.CAMPAIGN.FORM.WHATSAPP.NEW_TEMPLATE.NAME_HINT')
        "
        message-type="info"
      />

      <div class="flex gap-3">
        <!-- ComboBox has no label prop, unlike Input and TextArea. -->
        <div class="flex flex-col flex-1 gap-1">
          <span class="text-sm font-medium text-n-slate-12">
            {{ t('MARKETING.CAMPAIGN.FORM.WHATSAPP.NEW_TEMPLATE.CATEGORY') }}
          </span>
          <ComboBox
            v-model="state.category"
            :options="categoryOptions"
            class="[&>div>button]:bg-n-alpha-black2"
          />
        </div>
        <div class="flex flex-col flex-1 gap-1">
          <span class="text-sm font-medium text-n-slate-12">
            {{ t('MARKETING.CAMPAIGN.FORM.WHATSAPP.NEW_TEMPLATE.LANGUAGE') }}
          </span>
          <ComboBox
            v-model="state.language"
            :options="languageOptions"
            class="[&>div>button]:bg-n-alpha-black2"
          />
        </div>
      </div>

      <!-- 1024 is Meta's limit for a template body. -->
      <TextArea
        v-model="state.body"
        :label="t('MARKETING.CAMPAIGN.FORM.WHATSAPP.NEW_TEMPLATE.BODY')"
        :placeholder="
          t('MARKETING.CAMPAIGN.FORM.WHATSAPP.NEW_TEMPLATE.BODY_PLACEHOLDER')
        "
        :message="t('MARKETING.CAMPAIGN.FORM.WHATSAPP.NEW_TEMPLATE.BODY_HINT')"
        :max-length="1024"
        message-type="info"
        min-height="6rem"
        auto-height
        show-character-count
      />

      <template v-if="variables.length">
        <p class="text-xs text-n-slate-11">
          {{ t('MARKETING.CAMPAIGN.FORM.WHATSAPP.NEW_TEMPLATE.EXAMPLES_HINT') }}
        </p>
        <Input
          v-for="variable in variables"
          :key="variable"
          v-model="examples[variable]"
          :label="`{{${variable}}}`"
          :placeholder="
            t(
              'MARKETING.CAMPAIGN.FORM.WHATSAPP.NEW_TEMPLATE.EXAMPLE_PLACEHOLDER'
            )
          "
        />
      </template>

      <div v-if="state.body.trim()" class="flex flex-col gap-1">
        <span class="text-xs font-medium text-n-slate-12">
          {{ t('MARKETING.CAMPAIGN.FORM.WHATSAPP.NEW_TEMPLATE.PREVIEW') }}
        </span>
        <p
          class="p-3 text-sm whitespace-pre-line rounded-lg bg-n-alpha-2 text-n-slate-12"
        >
          {{ previewBody }}
        </p>
      </div>

      <p class="text-xs text-n-amber-11">
        {{ t('MARKETING.CAMPAIGN.FORM.WHATSAPP.NEW_TEMPLATE.REVIEW_NOTICE') }}
      </p>
    </div>
  </Dialog>
</template>
